import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
} from "lucide-react";
import { useTranslation } from 'react-i18next'

import { EntityLibraryFilterBar } from '@/components/entities/entity-library-filter-bar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  entityCardTypeDescriptionKeys,
  entityCardTypeLabelKeys,
  entityCardTypes,
  type EntityCardType,
  type EntityCardView,
  type EntityLibraryFilters,
} from '@/features/entities'
import { cn } from '@/lib/utils'

type EntityWorkspaceRailProps = {
  activeEntityId: string | null
  allowCreate?: boolean
  cards: EntityCardView[]
  collapsed?: boolean
  filters: EntityLibraryFilters
  isCreatingCard: boolean
  onClearFilters: () => void
  onCreateCard: (type: EntityCardType) => void
  onOpenCard: (entityId: string) => void
  onSearchTextChange: (value: string) => void
  onToggleCollapsed: () => void
  onTypeFilterChange: (value: EntityLibraryFilters['typeFilter']) => void
  selectedCardHiddenByFilters: boolean
  totalCount: number
}

export function EntityWorkspaceRail({
  activeEntityId,
  allowCreate = true,
  cards,
  collapsed = false,
  filters,
  isCreatingCard,
  onClearFilters,
  onCreateCard,
  onOpenCard,
  onSearchTextChange,
  onToggleCollapsed,
  onTypeFilterChange,
  selectedCardHiddenByFilters,
  totalCount,
}: EntityWorkspaceRailProps) {
  const { t } = useTranslation()
  const createButtons = entityCardTypes.map((type) => ({
    id: type,
    description: t(entityCardTypeDescriptionKeys[type]),
    label: t(entityCardTypeLabelKeys[type]),
    type,
  }));

  if (collapsed) {
    return (
      <aside className="flex items-start justify-center border-r border-ink/10 pr-1 pt-2 xl:sticky xl:top-28 xl:h-[calc(100vh-8rem)]">
        <div className="flex items-center gap-1">
          <span className="[writing-mode:vertical-rl] rotate-180 text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
            {t("entities.workspace.compactTitle")}
          </span>
          <button
            aria-label={t("entities.workspace.expandRail")}
            className="inline-flex h-8 items-center justify-center px-1 text-muted-foreground transition-colors hover:text-foreground"
            onClick={onToggleCollapsed}
            type="button"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </aside>
    )
  }

  return (
    <aside className="min-h-0 border-r border-ink/10 pr-4 xl:sticky xl:top-28 xl:h-[calc(100vh-8rem)] xl:max-h-[calc(100vh-8rem)] xl:overflow-hidden">
      <div className="flex h-full min-h-0 flex-col gap-6">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-clay">
              {t('entities.workspace.eyebrow')}
            </p>
            <h2 className="font-display text-3xl leading-none tracking-tight text-foreground">
              {t('entities.workspace.title')}
            </h2>
            <p className="text-sm leading-6 text-muted-foreground">
              {t("entities.workspace.sidebarBody")}
            </p>
          </div>
          <button
            aria-label={t("entities.workspace.collapseRail")}
            className="inline-flex h-8 items-center justify-center px-1 text-muted-foreground transition-colors hover:text-foreground"
            onClick={onToggleCollapsed}
            type="button"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <div className="space-y-6 pb-4">
            {allowCreate ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    className="h-12 w-full justify-between rounded-[1.1rem] bg-clay px-4 text-canvas shadow-none hover:bg-clay/90"
                    type="button"
                    variant={isCreatingCard ? "secondary" : "default"}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Plus className="h-4 w-4 shrink-0" />
                      {t("entities.workspace.createMenuLabel")}
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-[19rem] rounded-[1.35rem] border border-[#eadcca] bg-[#fffdf9] p-2 shadow-[0_22px_44px_-20px_rgba(40,31,19,0.38)] backdrop-blur-0"
                >
                  {createButtons.map((button) => (
                    <DropdownMenuItem
                      className="items-start rounded-[1rem] bg-transparent px-3 py-3 focus:bg-[#f6ede1]"
                      key={button.id}
                      onClick={() => onCreateCard(button.type)}
                    >
                      <div className="space-y-1">
                        <p className="font-medium text-foreground">{button.label}</p>
                        <p className="text-xs leading-5 text-muted-foreground">{button.description}</p>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}

            <EntityLibraryFilterBar
              filters={filters}
              onClearFilters={onClearFilters}
              onSearchTextChange={onSearchTextChange}
              onTypeFilterChange={onTypeFilterChange}
              resultCount={cards.length}
              totalCount={totalCount}
            />

            {selectedCardHiddenByFilters ? (
              <div className="rounded-[1.25rem] border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-950">
                {t('entities.filters.hiddenSelectionRail')}
              </div>
            ) : null}

            <div className="space-y-4">
              {cards.length ? (
                <div className="space-y-2">
                  {cards.map((card) => (
                    <button
                      key={card.id}
                      aria-current={card.id === activeEntityId ? 'page' : undefined}
                      className={cn(
                        'flex w-full items-start gap-3 rounded-[1.35rem] px-4 py-3 text-left transition-colors',
                        card.id === activeEntityId
                          ? 'bg-[linear-gradient(150deg,rgba(22,17,13,0.92),rgba(66,45,29,0.82))] text-canvas'
                          : 'bg-transparent text-muted-foreground hover:bg-[#f4eadc] hover:text-foreground',
                      )}
                      onClick={() => onOpenCard(card.id)}
                      type="button"
                    >
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="flex items-start justify-between gap-3">
                          <p
                            className={cn(
                              "truncate text-[1.05rem] font-medium",
                              card.id === activeEntityId ? "text-canvas" : "text-foreground/80",
                            )}
                          >
                            {card.name}
                          </p>
                          <span
                            className={cn(
                              "shrink-0 pt-0.5 text-[10px] font-semibold uppercase tracking-[0.16em]",
                              card.id === activeEntityId ? "text-canvas/70" : "text-muted-foreground",
                            )}
                          >
                            {t(entityCardTypeLabelKeys[card.type])}
                          </span>
                        </div>
                        <p
                          className={cn(
                            "line-clamp-2 text-xs leading-5",
                            card.id === activeEntityId ? "text-canvas/72" : "text-muted-foreground",
                          )}
                        >
                          {card.secondaryText}
                        </p>
                        <div
                          className={cn(
                            "flex flex-wrap items-center gap-2 text-[11px]",
                            card.id === activeEntityId ? "text-canvas/70" : "text-muted-foreground",
                          )}
                        >
                          <span>
                            {t('entities.relationships.count', {
                              count: card.relationshipCount,
                            })}
                          </span>
                          {card.hasImage ? <span>{t('entities.media.imageAttached')}</span> : null}
                          {card.hasBrokenLinks ? (
                            <span className={card.id === activeEntityId ? "text-[#f9d8b2]" : "text-destructive"}>
                              {t('entities.relationships.repairNeededShort')}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="rounded-[1.1rem] bg-[#f2e7d6]/55 px-3 py-4 text-center text-xs text-muted-foreground">
                  {totalCount
                    ? t('entities.emptyStates.noFilteredResults')
                    : t('entities.emptyStates.noCards')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </aside>
  )
}
